import { Routes } from '@angular/router';
import { AuthGuard } from './guards/auth.guard';
import { UnAuthGuard } from './guards/un-auth.guard';
import { EvaluationGuard } from './guards/evaluation.guard';

// Auth pages
import { PreAuthComponent } from './pages/auth/pre-auth/pre-auth.component';
import { SigninComponent } from './pages/auth/signin/signin.component';
import { RecoveryComponent } from './pages/auth/recovery/recovery.component';
import { RecoveryConfirmComponent } from './pages/auth/recovery-confirm/recovery-confirm.component';
import { LogoutComponent } from './pages/auth/logout/logout.component';

// Games pages
import { GamesSectionsComponent } from './pages/games/games-sections/games-sections.component';
import { GamesLevelsComponent } from './pages/games/games-levels/games-levels.component';
import { GamesLevelComponent } from './pages/games/games-level/games-level.component';
import { GamesGameComponent } from './pages/games/games-game/games-game.component';
import { GamesThemesComponent } from './pages/games/games-themes/games-themes.component';
import { GamesThemeLevelsComponent } from './pages/games/games-theme-levels/games-theme-levels.component';
import { GamesThemeLevelComponent } from './pages/games/games-theme-level/games-theme-level.component';

// Evaluation pages
import { EvaluationSelectProfileComponent } from './pages/evaluation/evaluation-select-profile/evaluation-select-profile.component';
import { EvaluationMessageComponent } from './pages/evaluation/evaluation-message/evaluation-message.component';
import { EvaluationQuestionsComponent } from './pages/evaluation/evaluation-questions/evaluation-questions.component';

// Exam pages
import { ExamIndexComponent } from './pages/exam/exam-index/exam-index.component';
import { ExamExamComponent } from './pages/exam/exam-exam/exam-exam.component';
import { ExamResultComponent } from './pages/exam/exam-result/exam-result.component';

// CEFRL exam pages
import { CefrlExamStateComponent } from './pages/cefrl-exam/cefrl-exam-state/cefrl-exam-state.component';
import { CefrlExamQuestionsComponent } from './pages/cefrl-exam/cefrl-exam-questions/cefrl-exam-questions.component';
import { CefrlExamResultComponent } from './pages/cefrl-exam/cefrl-exam-result/cefrl-exam-result.component';

// Assessment pages (test de fin de formation, hors parcours)
import { AssessmentStateComponent } from './pages/assessment/assessment-state/assessment-state.component';
import { AssessmentQuestionsComponent } from './pages/assessment/assessment-questions/assessment-questions.component';
import { AssessmentResultComponent } from './pages/assessment/assessment-result/assessment-result.component';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: '/fr/auth/signin'
  },
  {
    path: ':lang',
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'auth'
      },
      {
        path: 'auth',
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'signin'
          },
          {
            path: 'signin',
            component: SigninComponent,
            canActivate: [UnAuthGuard]
          },
          {
            path: 'logout',
            component: LogoutComponent
          }
        ]
      },
      {
        path: 'learning',
        canActivate: [AuthGuard, EvaluationGuard],
        loadChildren: () => import('./pages/learning/learning.routes').then(m => m.LEARNING_ROUTES)
      },
      {
        path: 'themes',
        canActivate: [AuthGuard, EvaluationGuard],
        children: [
          {
            path: '',
            canActivate: [AuthGuard],
            component: GamesThemesComponent,
          },
          {
            path: ':idTheme',
            canActivate: [AuthGuard],
            children: [
              {
                path: '',
                canActivate: [AuthGuard],
                component: GamesThemeLevelsComponent
              },
              {
                path: ':idLevel',
                canActivate: [AuthGuard],
                children: [
                  {
                    path: '',
                    canActivate: [AuthGuard],
                    component: GamesThemeLevelComponent,
                  },
                  {
                    path: ':idGame',
                    canActivate: [AuthGuard],
                    component: GamesGameComponent
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: 'games',
        canActivate: [AuthGuard, EvaluationGuard],
        children: [
          {
            path: '',
            canActivate: [AuthGuard],
            component: GamesSectionsComponent
          },
          {
            path: ':idSection',
            canActivate: [AuthGuard],
            children: [
              {
                path: '',
                canActivate: [AuthGuard],
                component: GamesLevelsComponent
              },
              {
                path: ':idLevel',
                canActivate: [AuthGuard],
                children: [
                  {
                    path: '',
                    canActivate: [AuthGuard],
                    component: GamesLevelComponent
                  },
                  {
                    path: ':idGame',
                    canActivate: [AuthGuard],
                    component: GamesGameComponent
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: 'exam',
        canActivate: [AuthGuard, EvaluationGuard],
        children: [
          {
            path: ':idLevel',
            canActivate: [AuthGuard],
            children: [
              {
                path: '',
                canActivate: [AuthGuard],
                component: ExamIndexComponent
              },
              {
                path: 'exam',
                canActivate: [AuthGuard],
                component: ExamExamComponent
              },
              {
                path: 'result',
                canActivate: [AuthGuard],
                component: ExamResultComponent
              }
            ]
          }
        ]
      },
      {
        path: 'cefrl-exam',
        canActivate: [AuthGuard, EvaluationGuard],
        children: [
          {
            path: ':idSection',
            canActivate: [AuthGuard],
            children: [
              {
                path: '',
                canActivate: [AuthGuard],
                component: CefrlExamStateComponent
              },
              {
                path: 'questions',
                canActivate: [AuthGuard],
                component: CefrlExamQuestionsComponent
              },
              {
                path: 'result',
                canActivate: [AuthGuard],
                component: CefrlExamResultComponent
              }
            ]
          }
        ]
      },
      {
        path: 'assessment',
        canActivate: [AuthGuard, EvaluationGuard],
        children: [
          {
            path: '',
            canActivate: [AuthGuard],
            component: AssessmentStateComponent
          },
          {
            path: 'questions',
            canActivate: [AuthGuard],
            component: AssessmentQuestionsComponent
          },
          {
            path: 'result',
            canActivate: [AuthGuard],
            component: AssessmentResultComponent
          }
        ]
      },
      {
        path: 'evaluation/:langTgt',
        canActivate: [AuthGuard],
        children: [
          {
            path: '',
            canActivate: [AuthGuard],
            component: EvaluationSelectProfileComponent
          },
          {
            path: 'start',
            canActivate: [AuthGuard],
            component: EvaluationMessageComponent
          },
          {
            path: 'questions',
            canActivate: [AuthGuard],
            component: EvaluationQuestionsComponent
          }
        ]
      }
    ]
  },
  {
    path: '**',
    redirectTo: '/fr/auth/signin'
  }
];
